import React, { useEffect, useState, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Alert, Animated, ActivityIndicator } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/types';
import { StrokeSyncService } from '../services/StrokeSyncService';
import { useAuthStore } from '../store/useAuthStore';
import { useDeviceStore } from '../store/useDeviceStore';
import { getOrCreateDeviceId } from '../utils/deviceId';
import { ChildFriendlyBackground } from '../components/ChildFriendlyBackground';
import { FriendlyButton } from '../components/FriendlyButton';
import { AudioPromptButton } from '../components/AudioPromptButton';
import { QrCode, Frown, ArrowRight } from 'lucide-react-native';
import { fetchWithRetry } from '../utils/fetchWithRetry';

type Props = StackScreenProps<RootStackParamList, 'Discovering'>;

export function DiscoveringScreen({ navigation, route }: Props) {
    const { classId, serverUrl, teacherCode: demoTeacherCode } = route.params;
    const [error, setError] = useState<string | null>(null);
    const [teacherCode, setTeacherCode] = useState('');
    const [needsTeacherCode, setNeedsTeacherCode] = useState(false);
    const [finalUrl, setFinalUrl] = useState<string>('');
    const [orgId, setOrgId] = useState<string>('');
    const [isValidating, setIsValidating] = useState(false);
    // Resolved DB class UUID from the join response — sent back to validate-teacher-code
    // so the issued device token is bound to the correct class.
    const [resolvedClassId, setResolvedClassId] = useState<string>('');
    const autoSubmitAttempted = useRef(false);

    const completeRegistration = (
        token: string,
        navServerUrl: string,
        registrationOrgId?: string,
        isDemoSession?: boolean
    ) => {
        useAuthStore.getState().setDeviceToken(token);
        // Demo sessions are ephemeral trials — never persist registration or mark the device
        // as a registered school tablet in useDeviceStore / AsyncStorage.
        if (registrationOrgId && !isDemoSession) {
            useDeviceStore.getState().setDeviceRegistration({
                orgId: registrationOrgId,
                schoolName: 'School Device',
                deviceToken: token,
                serverUrl: navServerUrl,
            }).catch(console.warn);
        }
        navigation.replace('RosterPick', { classId, serverUrl: navServerUrl });
    };

    // WP-029: submit the teacher code via validate-teacher-code and store the
    // device token returned by validate (NOT by join — join returns no token for
    // unregistered devices). Context (apiBase/orgId/classId) is passed explicitly
    // to avoid stale state during the demo auto-submit path (state setters are
    // async and not flushed within the same effect tick).
    const submitTeacherCode = async (
        code: string,
        ctx: { apiBase: string; org: string; classIdForToken: string; navServerUrl: string }
    ): Promise<void> => {
        const deviceId = await getOrCreateDeviceId();
        const validateUrl = `${ctx.apiBase}/api/devices/validate-teacher-code`;

        const res = await fetchWithRetry(validateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                deviceId,
                orgId: ctx.org,
                teacherCode: code,
                classId: ctx.classIdForToken,
            }),
            timeoutMs: 8000,
            maxRetries: 2,
        });

        if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.error || 'Invalid teacher code');
        }

        const data = await res.json();
        console.log(`[DiscoveringScreen] Device approved by ${data.approvedBy}`);

        // Store the token returned by validate-teacher-code (the join token is
        // null for new devices, so it must not be used here).
        if (data.deviceToken) {
            const isDemoSession = Boolean(demoTeacherCode || data.isDemo);
            completeRegistration(data.deviceToken, ctx.navServerUrl, ctx.org, isDemoSession);
        } else {
            navigation.replace('RosterPick', { classId, serverUrl: ctx.navServerUrl });
        }
    };

    useEffect(() => {
        let mounted = true;

        const checkDeviceAndConnect = async () => {
            try {
                // Step 1: Discover server
                const discoveredUrl = await StrokeSyncService.discoverAndSetServerUrl(serverUrl);
                if (!mounted) return;
                setFinalUrl(discoveredUrl);

                const apiBase = String(discoveredUrl).endsWith('/') ? String(discoveredUrl).slice(0, -1) : String(discoveredUrl);

                // Step 2: Get device ID
                const deviceId = await getOrCreateDeviceId();
                console.log(`[DiscoveringScreen] Device ID: ${deviceId}`);

                // Step 3: Join — WP-029: join now returns isRegistered directly and
                // issues a device token ONLY to already-registered devices. The
                // separate GET /api/devices/check-registration round-trip is dropped.
                const joinUrl = `${apiBase}/api/devices/join`;
                console.log(`[DiscoveringScreen] Joining device at ${joinUrl} (classId=${classId})`);
                const joinRes = await fetchWithRetry(joinUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ classId, deviceId }),
                    timeoutMs: 8000,
                    maxRetries: 2,
                });

                if (!joinRes.ok) {
                    throw new Error(`Device join failed: ${joinRes.status}`);
                }

                const joinData = await joinRes.json();
                if (!joinData.orgId) {
                    throw new Error('Invalid response from device join');
                }

                const discoveredOrgId = joinData.orgId;
                const discoveredClassId = joinData.classId || classId;
                setOrgId(discoveredOrgId);
                setResolvedClassId(discoveredClassId);

                // Step 4: Branch on isRegistered (returned by join directly).
                if (joinData.isRegistered && joinData.deviceToken) {
                    // Returning device — use the join token directly, no teacher code needed.
                    console.log(`[DiscoveringScreen] Device already registered, using join token`);
                    completeRegistration(joinData.deviceToken, discoveredUrl, discoveredOrgId);
                    return;
                }

                // New device — teacher code required. Do NOT store a null token.
                console.log('[DiscoveringScreen] New device detected, teacher code required');
                setNeedsTeacherCode(true);

                // WP-029 demo auto-submit: if a teacherCode was passed in (demo flow)
                // AND the device is unregistered, auto-submit the minted code so the
                // child doesn't have to type it. A returning demo device (isRegistered)
                // used the join token above and never reaches here — the minted code is
                // not wasted on returning devices.
                if (demoTeacherCode && !autoSubmitAttempted.current) {
                    autoSubmitAttempted.current = true;
                    setIsValidating(true);
                    try {
                        await submitTeacherCode(demoTeacherCode, {
                            apiBase,
                            org: discoveredOrgId,
                            classIdForToken: discoveredClassId,
                            navServerUrl: discoveredUrl,
                        });
                    } catch (err: unknown) {
                        console.warn('[DiscoveringScreen] Demo auto-submit failed:', err);
                        // Gentle guidance for children — the child presses "Try Demo"
                        // again on HomeScreen, which mints a fresh code. Never silent.
                        if (mounted) {
                            setError("Let's try again!");
                        }
                    } finally {
                        if (mounted) setIsValidating(false);
                    }
                }
            } catch (err: unknown) {
                console.error('[DiscoveringScreen] Connection error:', err);
                const msg = (err as Error).message || '';
                let friendly = 'Failed to connect to classroom: ' + msg;
                if (msg.includes('unreachable') || msg.includes('ENOTFOUND') || msg.includes('Could not resolve')) {
                    friendly += '\n\nTip: Try turning Airplane Mode ON then OFF to refresh the network connection, then try again.';
                }
                if (mounted) {
                    setError(friendly);
                }
            }
        };

        checkDeviceAndConnect();

        return () => {
            mounted = false;
        };
        // react-hooks/exhaustive-deps is not registered in @eduwrite/eslint-config,
        // so a disable directive for it is dead and errors under
        // reportUnusedDisableDirectives. The effect intentionally runs only on
        // [classId, serverUrl, navigation]; demoTeacherCode/submitTeacherCode are
        // excluded to avoid re-running join/auto-submit on every render.
    }, [classId, serverUrl, navigation]);

    const validateTeacherCode = async () => {
        if (!teacherCode || teacherCode.length !== 6) {
            Alert.alert('Invalid Code', 'Please enter a 6-digit teacher code');
            return;
        }

        setIsValidating(true);

        try {
            const apiBase = String(finalUrl).endsWith('/') ? String(finalUrl).slice(0, -1) : String(finalUrl);
            await submitTeacherCode(teacherCode, {
                apiBase,
                org: orgId,
                classIdForToken: resolvedClassId || classId,
                navServerUrl: finalUrl || serverUrl,
            });
        } catch (err: unknown) {
            Alert.alert('Validation Failed', (err as Error).message);
        } finally {
            setIsValidating(false);
        }
    };

    const bounceAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        if (error || needsTeacherCode) return;
        const anim = Animated.loop(
            Animated.sequence([
                Animated.timing(bounceAnim, { toValue: 1, duration: 600, useNativeDriver: true }),
                Animated.timing(bounceAnim, { toValue: 0, duration: 600, useNativeDriver: true }),
            ])
        );
        anim.start();
        return () => anim.stop();
    }, [error, needsTeacherCode]);

    const mascotScale = bounceAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [1, 1.25],
    });

    // Teacher Code Input UI
    // WP-033: in the demo flow (teacherCode passed in route params), when the
    // device is unregistered and the auto-submit is in flight, show a child-
    // friendly "Connecting to your classroom…" loading state instead of the
    // "Ask your teacher for the 6-digit code" input form. The child never sees
    // the code-entry form in the demo flow.
    if (needsTeacherCode) {
        // Demo flow — show loading state, not the code-entry form.
        if (demoTeacherCode) {
            return (
                <ChildFriendlyBackground variant="warm">
                    <View style={styles.content}>
                        <Animated.View style={{ transform: [{ scale: mascotScale }] }}>
                            <Text style={styles.mascotEmoji} allowFontScaling={true} maxFontSizeMultiplier={1.5}>🦊</Text>
                        </Animated.View>
                        <Text style={styles.loadingText} accessibilityLiveRegion="polite" allowFontScaling={true} maxFontSizeMultiplier={1.5}>Connecting to your classroom…</Text>
                        <Text style={styles.subText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Class: {classId}</Text>
                        <ActivityIndicator size="large" color="#1890FF" style={{ marginTop: 16 }} />
                    </View>
                </ChildFriendlyBackground>
            );
        }

        // Non-demo flow — show the code-entry form for a real new device.
        return (
            <ChildFriendlyBackground variant="warm">
                <View style={styles.content}>
                    <View style={styles.iconContainer}>
                        <QrCode size={48} color="#1890FF" strokeWidth={2.5} />
                    </View>

                    <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.5}>New Device</Text>
                    <Text style={styles.subtitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Ask your teacher for the 6-digit code</Text>

                    <AudioPromptButton text="Ask your teacher for the 6-digit code" autoPlay={false} />

                    <TextInput
                        style={styles.codeInput}
                        value={teacherCode}
                        onChangeText={setTeacherCode}
                        placeholder="------"
                        placeholderTextColor="#94a3b8"
                        keyboardType="number-pad"
                        maxLength={6}
                        autoFocus
                        accessibilityLabel="Teacher code input"
                        accessibilityHint="Enter the 6-digit code your teacher gave you"
                    />

                    <TouchableOpacity
                        style={[styles.button, isValidating && styles.buttonDisabled]}
                        onPress={validateTeacherCode}
                        disabled={isValidating}
                        activeOpacity={0.8}
                        accessibilityLabel="Continue"
                        accessibilityRole="button"
                        accessibilityHint="Submit the teacher code to join the class"
                    >
                        <Text style={styles.buttonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            {isValidating ? 'Checking...' : 'Continue'}
                        </Text>
                        {!isValidating && <ArrowRight size={20} color="#FFFFFF" strokeWidth={3} />}
                    </TouchableOpacity>

                    <TouchableOpacity style={styles.cancelButton} onPress={() => navigation.goBack()} accessibilityLabel="Go Back" accessibilityRole="button" accessibilityHint="Go back to the previous screen">
                        <Text style={styles.cancelText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>← Go Back</Text>
                    </TouchableOpacity>
                </View>
            </ChildFriendlyBackground>
        );
    }

    // Loading or Error UI
    return (
        <ChildFriendlyBackground variant="warm">
            <View style={styles.content}>
                {error ? (
                    <>
                        <View style={styles.iconContainer}>
                            <Frown size={48} color="#FAAD14" strokeWidth={2.5} />
                        </View>
                        <Text style={styles.errorText} accessibilityLiveRegion="polite" allowFontScaling={true} maxFontSizeMultiplier={1.5}>{error}</Text>
                        <AudioPromptButton text="Let's try again!" autoPlay={false} />
                        <View style={{ height: 16 }} />
                        <FriendlyButton
                            variant="primary"
                            size="medium"
                            onPress={() => navigation.goBack()}
                        >
                            Let's Try Again
                        </FriendlyButton>
                    </>
                ) : (
                    <>
                        <Animated.View style={{ transform: [{ scale: mascotScale }] }}>
                            <Text style={styles.mascotEmoji} allowFontScaling={true} maxFontSizeMultiplier={1.5}>🦊</Text>
                        </Animated.View>
                        <Text style={styles.loadingText} accessibilityLiveRegion="polite" allowFontScaling={true} maxFontSizeMultiplier={1.5}>Connecting to Classroom...</Text>
                        <Text style={styles.subText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Class: {classId}</Text>
                    </>
                )}
            </View>
        </ChildFriendlyBackground>
    );
}

const styles = StyleSheet.create({
    mascotEmoji: {
        fontSize: 64,
        marginBottom: 16,
    },
    blob: {
        position: 'absolute',
        borderRadius: 999,
        opacity: 0.35,
    },
    blobTopRight: {
        width: 140,
        height: 140,
        backgroundColor: '#BAE7FF',
        top: 60,
        right: -30,
    },
    blobBottomLeft: {
        width: 120,
        height: 120,
        backgroundColor: '#FFD8BF',
        bottom: 120,
        left: -20,
    },
    content: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
        width: '100%',
    },
    iconContainer: {
        width: 96,
        height: 96,
        borderRadius: 48,
        backgroundColor: '#FFF7E6',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 24,
        shadowColor: '#FAAD14',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 4,
    },
    loadingText: { marginTop: 24, fontSize: 20, fontWeight: '700', color: '#1E293B', textAlign: 'center' },
    subText: { marginTop: 8, fontSize: 16, color: '#64748B', textAlign: 'center' },
    errorText: {
        color: '#475569',
        fontSize: 16,
        textAlign: 'center',
        paddingHorizontal: 20,
        marginTop: 16,
        lineHeight: 24,
    },
    button: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 24,
        backgroundColor: '#52C41A',
        paddingHorizontal: 32,
        paddingVertical: 16,
        borderRadius: 16,
        minHeight: 56,
        gap: 8,
        shadowColor: '#52C41A',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 5,
    },
    buttonText: {
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: '700',
    },
    buttonDisabled: {
        backgroundColor: '#94a3b8',
        shadowOpacity: 0,
        elevation: 0,
    },
    // Teacher code styles
    title: { fontSize: 28, fontWeight: '800', color: '#1E293B', marginBottom: 8, textAlign: 'center' },
    subtitle: { fontSize: 16, color: '#64748B', marginBottom: 32, textAlign: 'center' },
    codeInput: {
        width: 240,
        height: 72,
        borderWidth: 2,
        borderColor: '#52C41A',
        borderRadius: 16,
        fontSize: 28,
        fontWeight: '700',
        textAlign: 'center',
        textAlignVertical: 'center',
        color: '#1E293B',
        letterSpacing: 6,
        backgroundColor: '#FFFFFF',
        marginBottom: 32,
    },
    cancelButton: {
        marginTop: 20,
        paddingVertical: 12,
        paddingHorizontal: 24,
        minHeight: 48,
    },
    cancelText: {
        color: '#64748B',
        fontSize: 16,
        fontWeight: '600',
    },
});
